<template>
  <div class="side-donut" :class="{'is-readonly': !selectable}">
    <div class="side-donut-plate" :style="plateStyle" aria-hidden="true"></div>

    <header class="side-donut-head">
      <p class="side-donut-round">
        <span class="side-donut-tick" aria-hidden="true"></span>
        {{ roundLabel }}
        <span class="side-donut-tick" aria-hidden="true"></span>
      </p>
      <h2 class="side-donut-place">{{ placeLabel }}</h2>
      <p class="side-donut-title">{{ selectable ? $t('fightHud.donutTitle') : $t('fightHud.donutWatchTitle') }}</p>
    </header>

    <div class="side-donut-stage">
      <div class="side-donut-canvas">
        <svg viewBox="0 0 100 100" class="side-donut-svg" aria-hidden="true">
          <circle class="side-donut-track" cx="50" cy="50" r="39" />
          <g
              v-for="arc in arcs"
              :key="arc.id"
              class="side-donut-wedge"
              :class="{'is-selected': arc.id === selectedSideId}"
              :style="wedgeStyle(arc.side)"
              role="radio"
              :tabindex="selectable ? 0 : -1"
              :aria-checked="arc.id === selectedSideId"
              :aria-label="wedgeLabel(arc.side)"
              @click="selectSide(arc.side)"
              @keydown.enter.prevent="selectSide(arc.side)"
              @keydown.space.prevent="selectSide(arc.side)"
          >
            <path :d="arc.path" class="side-donut-arc" />
          </g>
        </svg>

        <div class="side-donut-center">
          <CountDown v-if="deadline" :deadline="deadline" :skew-ms="skewMs" @expired="$emit('expired')" />
          <p class="side-donut-headcount">
            {{ $t('fightHud.donutHeadcount', {count: headcount}) }}
          </p>
        </div>
      </div>

      <!-- The sides stand around the donut: name, who leads, who is on it.
           A grid rather than a radial layout so five sides with long member
           lists stay readable, and so it reflows to one column on a phone. -->
      <ul class="side-donut-sides">
        <li
            v-for="card in sideCards"
            :key="card.id"
            class="side-card"
            :class="{'is-selected': card.id === selectedSideId, 'is-mine': card.isMine}"
            :style="wedgeStyle(card.side)"
            role="radio"
            :tabindex="selectable ? 0 : -1"
            :aria-checked="card.id === selectedSideId"
            :aria-label="wedgeLabel(card.side)"
            @click="selectSide(card.side)"
            @keydown.enter.prevent="selectSide(card.side)"
            @keydown.space.prevent="selectSide(card.side)"
        >
          <p class="side-card-name">
            <span class="side-donut-swatch" aria-hidden="true"></span>
            {{ card.side.name }}
          </p>

          <p class="side-card-lead">
            <span class="side-card-lead-mark" aria-hidden="true">&#9670;</span>
            {{ card.lead ? card.lead.name : $t('fightHud.donutNoLead') }}
          </p>

          <ul v-if="card.others.length" class="side-card-members">
            <li v-for="member in card.others" :key="member.character_id" class="side-card-member">
              {{ member.name }}
            </li>
          </ul>
          <p v-else class="side-card-members-empty">{{ $t('fightHud.donutNoMembers') }}</p>

          <dl class="side-card-stats">
            <div class="side-card-stat">
              <dt>{{ $t('fightHud.donutStatFighters') }}</dt>
              <dd>{{ card.side.member_count }}</dd>
            </div>
            <div class="side-card-stat">
              <dt>{{ $t('fightHud.donutStatPower') }}</dt>
              <dd>{{ card.side.power }}</dd>
            </div>
            <div class="side-card-stat">
              <dt>{{ $t('fightHud.donutStatShare') }}</dt>
              <dd>{{ sharePercent({share: card.side.power_share}) }}%</dd>
            </div>
          </dl>
        </li>
      </ul>
    </div>

    <HudButton
        v-if="canCreateSide"
        :type="ButtonType.SUBMIT"
        :disabled="submitting"
        class="side-donut-new"
        @click="createSide"
    >
      {{ $t('fightHud.newSide') }}
    </HudButton>

    <p v-if="error" class="side-donut-error">{{ error }}</p>
  </div>
</template>

<script>
import CountDown from '@/components/Game/Fight/CountDown.vue';
import HudButton from '@/components/btn/HudButton.vue';
import {ButtonType} from '@/types/ButtonType';
import {buildSideArcs} from '@/utils/sideDonutGeometry';
import {buildSideCards} from '@/utils/fightSides';
import {sideColorVar} from '@/utils/sideColor';

const MAX_FIGHT_SIDES = 5;

/*
 * Dumb component, following `FightChallengeOverlay.vue`'s precedent: no
 * generated API class is ever called from here. Selecting an arc or
 * confirming the "new side" affordance only emits an intent — `Location.vue`
 * owns the `choose_side`/`create_side` calls (through `FightService`) and
 * reports the outcome back through `fail()`/`reset()`, the same shape the
 * challenge overlay uses.
 */
export default {
  name: 'SideSelectionDonut',
  components: {CountDown, HudButton},
  setup() {
    return {ButtonType};
  },
  props: {
    sides: {
      type: Array,
      default: () => [],
    },
    selectedSideId: {
      type: String,
      default: null,
    },
    deadline: {
      type: [String, Number, Date],
      default: null,
    },
    skewMs: {
      type: Number,
      default: 0,
    },
    /* Everyone the clock reports, `FightStateDTO.participants`. Members are
       derived from these by grouping on `side_id` rather than being sent a
       second time on every side: the names are already here, and a second
       copy could only ever disagree with the first. Only the lead comes off
       the side itself, as `lead_character_id`, because nothing else on the
       payload records who founded a side. */
    participants: {
      type: Array,
      default: () => [],
    },
    /* False for a viewer who is in the establishment window but has no
       choice to make — a founder, or anyone already seated. They see the
       fight forming; the server would refuse them `choose_side`, so the
       screen must not offer it. */
    selectable: {
      type: Boolean,
      default: true,
    },
    selfCharacterId: {
      type: String,
      default: null,
    },
    locationName: {
      type: String,
      default: null,
    },
    round: {
      type: Number,
      default: null,
    },
    backgroundUrl: {
      type: String,
      default: '',
    },
  },
  emits: ['select-side', 'create-side', 'expired'],
  data() {
    return {
      submitting: false,
      error: null,
    };
  },
  computed: {
    arcs() {
      return buildSideArcs(this.sides);
    },
    canCreateSide() {
      return this.selectable !== false && this.sides.length < MAX_FIGHT_SIDES;
    },
    /* One card per side: the side itself, its lead, and the rest of its
       members, grouped from `participants` by `side_id`. Shared with the
       player's establishment screen through `utils/fightSides.js`, so the GM
       panel and the stage never disagree about who is on a side. */
    sideCards() {
      return buildSideCards(this.arcs, this.participants, {
        selfCharacterId: this.selfCharacterId,
        unknownLeadName: this.$t('fightHud.donutUnknownLead'),
      });
    },
    headcount() {
      return (this.participants || []).length;
    },
    placeLabel() {
      return this.locationName || this.$t('fightStart.unknownLocation');
    },
    roundLabel() {
      return this.$t('fightStart.round', {round: this.round ?? 1});
    },
    plateStyle() {
      return this.backgroundUrl
          ? {backgroundImage: `url(${this.backgroundUrl})`}
          : {};
    },
  },
  methods: {
    selectSide(side) {
      if (this.submitting || !side || this.selectable === false) {
        return;
      }
      this.submitting = true;
      this.error = null;
      this.$emit('select-side', side.id);
    },
    createSide() {
      if (this.submitting || !this.canCreateSide) {
        return;
      }
      this.submitting = true;
      this.error = null;
      this.$emit('create-side');
    },
    /* Colour is a fixed 1-5 slot, never a hue picked from the side's own
       data, so a side keeps its colour across the donut, the status rail and
       the board rings. Shared with `FightStatusRail.vue` and
       `PlayerTacticalMap.vue` via `utils/sideColor.js`, so a side never wears
       one colour on the rail and another here. */
    sideColorVar(slot) {
      return sideColorVar(slot);
    },
    wedgeStyle(side) {
      return {'--side-donut-color': this.sideColorVar(side?.slot)};
    },
    sharePercent(arc) {
      return Math.round((arc.share ?? 0) * 100);
    },
    wedgeLabel(side) {
      return this.$t('fightHud.donutSideAriaLabel', {
        name: side.name,
        count: side.member_count,
        percent: this.sharePercent({share: side.power_share}),
      });
    },
    // Exposed for the caller, same shape as `FightChallengeOverlay`: this
    // component performs no I/O of its own, so whoever owns the
    // choose/create-side service call reports the outcome back through these.
    fail(message) {
      this.submitting = false;
      this.error = message || this.$t('fightHud.error');
    },
    reset() {
      this.submitting = false;
      this.error = null;
    },
  },
};
</script>

<style scoped>
.side-donut {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  padding: clamp(1rem, 3vw, 2.5rem);
  overflow: hidden;
}

/* The scene behind the establishment screen. Empty until the fight-scene
   bank lands in `assets/images/backgrounds/fight`, at which point
   `useLocationBackground('fight', …)` fills it; the painted gradient below is what
   shows through until then, and if the bank is ever removed. */
.side-donut-plate {
  position: absolute;
  inset: 0;
  background:
      radial-gradient(circle at 50% 30%, rgba(64, 108, 130, 0.22), transparent 62%),
      linear-gradient(160deg, #050c12 0%, #0a151d 55%, #05090d 100%);
  background-size: cover;
  background-position: center;
  opacity: 0.9;
  pointer-events: none;
}

.side-donut > *:not(.side-donut-plate) {
  position: relative;
  z-index: 1;
}

.side-donut-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  text-align: center;
}

.side-donut-round {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0;
  font-size: 0.7rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: #8fa3ad;
}

.side-donut-tick {
  width: 1.6rem;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(211, 224, 230, 0.6), transparent);
}

.side-donut-place {
  margin: 0;
  font-family: var(--pl-font-display, var(--gm-font-display));
  font-size: clamp(1.4rem, 3.4vw, 2.4rem);
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #eef4f6;
}

.side-donut-stage {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: clamp(1rem, 3vw, 2.5rem);
  width: 100%;
}

.side-donut-headcount {
  margin: 0.35rem 0 0;
  font-size: 0.6rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #8fa3ad;
}

.side-donut-sides {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0.75rem;
  flex: 1 1 22rem;
  max-width: 46rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.side-card {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid rgba(211, 224, 230, 0.16);
  border-left: 3px solid var(--side-donut-color);
  border-radius: 0.35rem;
  background: rgba(4, 10, 15, 0.62);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.side-donut.is-readonly .side-card,
.side-donut.is-readonly .side-donut-wedge {
  cursor: default;
}

.side-card:hover,
.side-card:focus-visible,
.side-card.is-selected {
  border-color: var(--side-donut-color);
  background: rgba(9, 20, 28, 0.85);
}

.side-donut.is-readonly .side-card:hover {
  border-color: rgba(211, 224, 230, 0.16);
  border-left-color: var(--side-donut-color);
  background: rgba(4, 10, 15, 0.62);
}

.side-card.is-mine {
  box-shadow: inset 0 0 0 1px var(--side-donut-color);
}

.side-card-name {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  font-family: var(--pl-font-display, var(--gm-font-display));
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #eef4f6;
}

.side-card-lead {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0;
  font-size: 0.72rem;
  color: #d3e0e6;
}

.side-card-lead-mark {
  color: var(--side-donut-color);
  font-size: 0.6rem;
}

.side-card-members {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.side-card-member {
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  border: 1px solid rgba(211, 224, 230, 0.14);
  font-size: 0.62rem;
  color: #b7c3c9;
  white-space: nowrap;
}

.side-card-members-empty {
  margin: 0;
  font-size: 0.62rem;
  font-style: italic;
  color: #7d8a91;
}

.side-card-stats {
  display: flex;
  gap: 0.75rem;
  margin: 0.15rem 0 0;
}

.side-card-stat dt {
  font-size: 0.52rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #7d8a91;
}

.side-card-stat dd {
  margin: 0;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  color: #eef4f6;
}

.side-donut-title {
  margin: 0;
  font-family: var(--pl-font-display, var(--gm-font-display));
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--pl-track-title, 0.1em);
  color: var(--pl-text, #d3e0e6);
  text-align: center;
}

.side-donut-canvas {
  position: relative;
  flex: 0 0 auto;
  width: clamp(12rem, 22vw, 18rem);
  aspect-ratio: 1;
}

.side-donut-svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.side-donut-track {
  fill: none;
  stroke: rgba(211, 224, 230, 0.12);
  stroke-width: 0.5;
}

.side-donut-wedge {
  cursor: pointer;
  outline: none;
}

.side-donut-arc {
  fill: var(--side-donut-color);
  opacity: 0.72;
  stroke: rgba(2, 8, 14, 0.6);
  stroke-width: 0.6;
  transition: opacity 0.15s ease;
}

.side-donut-wedge:hover .side-donut-arc,
.side-donut-wedge:focus-visible .side-donut-arc {
  opacity: 0.92;
}

.side-donut-wedge:focus-visible .side-donut-arc {
  stroke: #eef4f6;
  stroke-width: 1.4;
}

.side-donut-wedge.is-selected .side-donut-arc {
  opacity: 1;
  stroke: #eef4f6;
  stroke-width: 1.4;
}

.side-donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}







.side-donut-swatch {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: var(--side-donut-color);
  box-shadow: 0 0 0.4rem var(--side-donut-color);
  flex-shrink: 0;
}



.side-donut-new {
  min-width: 8rem;
}

.side-donut-error {
  margin: 0;
  color: var(--gm-danger, #e2726c);
  font-size: 0.75rem;
}
</style>
